import { Box, Link, Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareInputRepeater } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import {
  FormAwareSelectInput,
  InputOption,
} from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Callout } from '@/components/notifications/Callout/Callout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import {
  McpAccess,
  OrganizationApplicationConfigurationMaxSessionDuration,
  OrganizationApplicationConfigurationUserSessionInactivityTimeout,
  PresentationApprovalRequirement,
  TenantApplicationConfigurationNonSsoLoginMechanism,
  TenantApplicationConfigurationUserSessionInactivityTimeout,
} from '@/types/schema';
import { validateDomainName } from '@/utils/validators/validateDomainName';

export interface ApplicationConfigurationFormShape {
  allowUserPresentationTemplates: boolean;
  collaboratorDomainAllowList: { value: string }[];
  disableAdvancedAnalytics: boolean;
  disableAiCapabilities: boolean;
  disableAiInsights: boolean;
  disableRoleManagement: boolean;
  disclaimerConfigurationOverridesDescendants: boolean;
  enableEmployeeIdpId: boolean;
  maxSessionDuration: OrganizationApplicationConfigurationMaxSessionDuration;
  mcpAccess: McpAccess;
  nonSsoLoginMechanism: TenantApplicationConfigurationNonSsoLoginMechanism;
  requirePresentationApproval: PresentationApprovalRequirement;
  smartReviewEnabled: boolean;
  userSessionInactivityTimeout:
    | OrganizationApplicationConfigurationUserSessionInactivityTimeout
    | TenantApplicationConfigurationUserSessionInactivityTimeout;
}

const SESSION_INACTIVITY_TIMEOUT_OPTIONS: InputOption[] = [
  {
    value: OrganizationApplicationConfigurationUserSessionInactivityTimeout.Off,
    display: 'Off',
  },
  {
    value:
      OrganizationApplicationConfigurationUserSessionInactivityTimeout.FifteenMinutes,
    display: '15 minutes',
  },
  {
    value:
      OrganizationApplicationConfigurationUserSessionInactivityTimeout.OneHour,
    display: '60 minutes',
  },
];

const MAX_SESSION_DURATION_OPTIONS: InputOption[] = [
  {
    value: OrganizationApplicationConfigurationMaxSessionDuration.OneHour,
    display: '1 hour',
  },
  {
    value: OrganizationApplicationConfigurationMaxSessionDuration.EightHours,
    display: '8 hours',
  },
  {
    value: OrganizationApplicationConfigurationMaxSessionDuration.TwelveHours,
    display: '12 hours',
  },
  {
    value:
      OrganizationApplicationConfigurationMaxSessionDuration.TwentyEightDays,
    display: '28 days',
  },
];

const MCP_ACCESS_OPTIONS: InputOption[] = [
  {
    value: McpAccess.Off,
    display: 'Off',
  },
  {
    value: McpAccess.Read,
    display: 'Read only',
  },
  {
    value: McpAccess.ReadWrite,
    display: 'Read and write',
  },
];

const NON_SSO_LOGIN_METHOD_OPTIONS: InputOption[] = [
  {
    value: TenantApplicationConfigurationNonSsoLoginMechanism.MagicCode,
    display: 'Magic Code',
  },
  {
    value: TenantApplicationConfigurationNonSsoLoginMechanism.MagicLink,
    display: 'Magic Link',
  },
];

interface ApplicationConfigurationFormFieldsProps {
  configurationLevel: 'organization' | 'tenant';
  loading: boolean;
  readOnly?: boolean;
}

export function ApplicationConfigurationFormFields({
  configurationLevel,
  loading,
  readOnly = false,
}: ApplicationConfigurationFormFieldsProps) {
  const { control } = useFormContext<ApplicationConfigurationFormShape>();
  const isOrganizationConfiguration = configurationLevel === 'organization';

  return (
    <Stack spacing={2} maxWidth={400}>
      {!readOnly && (
        <Callout severity="info-high">
          Updates here require an app refresh in order to take effect for the
          current user
        </Callout>
      )}
      <FormAwareCheckbox
        control={control}
        label="Disable AI functionality"
        fieldName="disableAiCapabilities"
      />
      <FormAwareCheckbox
        control={control}
        label="Disable AI insights"
        helpText="When enabled, the Estate Insights section is hidden for all users"
        fieldName="disableAiInsights"
      />
      <FormAwareCheckbox
        control={control}
        label="Enable SmartReview"
        helpText="Controls whether SmartReview is available for new client onboarding"
        fieldName="smartReviewEnabled"
      />
      {isOrganizationConfiguration && (
        <OrganizationOnlyFieldsBeforeEmployeeIdp control={control} />
      )}
      <FormAwareCheckbox
        control={control}
        label="Allow set/edit employee's IdP ID"
        contextualHelp={
          <PopperContent
            body={
              <Link
                href="https://app.notion.com/p/withluminary/Enterprise-Features-2a265ff1a4e280a9aa98f8a09abd64aa?source=copy_link#39765ff1a4e280f6b1c0e7b6624ea5c4"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </Link>
            }
          />
        }
        fieldName="enableEmployeeIdpId"
      />
      <FormAwareSelectInput
        control={control}
        showEmptyValue={false}
        required
        options={MCP_ACCESS_OPTIONS}
        label="MCP access"
        helpText="Controls whether connected MCP clients can read or update Luminary data"
        fieldName="mcpAccess"
      />
      <FormAwareSelectInput
        control={control}
        showEmptyValue={false}
        required
        options={SESSION_INACTIVITY_TIMEOUT_OPTIONS}
        label="Session timeout duration"
        helpText="The amount of time before a user is logged out of the app"
        fieldName="userSessionInactivityTimeout"
      />
      {isOrganizationConfiguration ? (
        <FormAwareSelectInput
          control={control}
          showEmptyValue={false}
          required
          options={MAX_SESSION_DURATION_OPTIONS}
          label="Maximum session duration"
          helpText="The maximum duration of a user session"
          fieldName="maxSessionDuration"
        />
      ) : (
        <FormAwareSelectInput
          control={control}
          showEmptyValue={false}
          required
          options={NON_SSO_LOGIN_METHOD_OPTIONS}
          label="Non-SSO login method"
          helpText="The login method to use when SSO is not enabled"
          fieldName="nonSsoLoginMechanism"
        />
      )}
      <CollaboratorDomainAllowList control={control} />
      {!readOnly && (
        <Box>
          <Button loading={loading} variant="primary" type="submit" size="sm">
            Save updates
          </Button>
        </Box>
      )}
    </Stack>
  );
}

interface FormControlProps {
  control: ReturnType<
    typeof useFormContext<ApplicationConfigurationFormShape>
  >['control'];
}

function OrganizationOnlyFieldsBeforeEmployeeIdp({
  control,
}: FormControlProps) {
  return (
    <>
      <FormAwareCheckbox
        control={control}
        label="Disable advanced analytics"
        fieldName="disableAdvancedAnalytics"
      />
      <FormAwareCheckbox
        control={control}
        label="Disable role management"
        helpText="Member tenants cannot create, update, or delete roles; roles are org-managed."
        fieldName="disableRoleManagement"
      />
      <FormAwareCheckbox
        control={control}
        label="Override descendant disclaimer configuration"
        helpText="Organization disclaimer values take precedence over member tenant and sub-brand values. Empty organization text values fall back to descendant values."
        fieldName="disclaimerConfigurationOverridesDescendants"
      />
    </>
  );
}

function CollaboratorDomainAllowList({ control }: FormControlProps) {
  return (
    <Box>
      <Typography variant="title-xs">Collaborator domain allow list</Typography>
      <Typography variant="body-xs">
        Restrict which email domains can be invited as collaborators. Leave
        empty to allow all domains.
      </Typography>
      <FormAwareInputRepeater<ApplicationConfigurationFormShape>
        name="collaboratorDomainAllowList"
        control={control}
        allowEmpty
        isSingleLineRepeatedInput={false}
        emptyValue={{ value: '' }}
        addAnotherItemText="Add domain"
        render={(index) => (
          <FormAwareTextInput<ApplicationConfigurationFormShape>
            control={control}
            fieldName={`collaboratorDomainAllowList.${index}.value`}
            label=""
            placeholder="example.com"
            validation={{ domainName: validateDomainName }}
          />
        )}
      />
    </Box>
  );
}
